import { useState } from "preact/hooks";
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Divider,
  LinearProgress, Stack, Typography, Chip, Tooltip, Grid, IconButton,
  Select, MenuItem, TextField
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PaymentIcon from "@mui/icons-material/Payment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GetAppIcon from '@mui/icons-material/GetApp';
import { usePaymentsStore } from "./store";
import { ConfirmActionDialog } from "@shared/components";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  overflow: "hidden",
}));

const ScrollArea = styled("div")(({ theme }) => ({
  maxHeight: 400,
  overflowY: "auto",
  paddingRight: theme.spacing(1),
}));

function fmtDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "—" : date.toLocaleDateString("es-MX");
}
function fmtDateTime(d) {
  if (!d) return "—";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "—" : date.toLocaleString("es-MX");
}
function fmtMoney(amount, currency = "MXN") {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency }).format(Number(amount || 0));
}

function StatusChip({ status }) {
  const S = styled("span")(({ theme }) => ({
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.common.white,
  }));
  if (status === "paid") return <S sx={{ bgcolor: "success.main" }}>Pagado</S>;
  if (status === "pending") return <S sx={{ bgcolor: "warning.main" }}>Pendiente</S>;
  if (status === "late") return <S sx={{ bgcolor: "error.main" }}>Atrasado</S>;
  return <S sx={{ bgcolor: "grey.500" }}>—</S>;
}

function AdminActions() {
  const { saving, registerOpen, openRegister, closeRegister, registerPayment, payments } = usePaymentsStore();
  const [paymentToRegister, setPaymentToRegister] = useState("");
  const [paymentNote, setPaymentNote] = useState("");

  const onRegisterPaymentSubmit = () => {
    if (!paymentToRegister) {
      toast.error("Selecciona un pago para registrar.");
      return;
    }
    registerPayment({
      paymentId: paymentToRegister,
      notes: paymentNote,
    });
  };

  return (
    <>
      <Button size="small" variant="contained" color="secondary" onClick={openRegister}>
        Registrar pago manual
      </Button>
      <ConfirmActionDialog
        open={registerOpen}
        onClose={closeRegister}
        action={onRegisterPaymentSubmit}
        label="Registrar"
        title="Registrar pago manual"
        description="Selecciona el pago que deseas registrar y añade una nota si es necesario."
        loading={saving}
        confirmVariant="contained"
      >
        <Select
          value={paymentToRegister}
          onChange={(e) => setPaymentToRegister(e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
        >
          {payments
            ?.filter((p) => p.status !== "paid")
            .map((p) => (
              <MenuItem key={p._id} value={p._id}>
                #{p.paymentNo} · {p.kind === "advance" ? "Anticipo" : "Hito"}
              </MenuItem>
            ))}
        </Select>
        <TextField
          label="Nota (opcional)"
          value={paymentNote}
          onChange={(e) => setPaymentNote(e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
        />
      </ConfirmActionDialog>
    </>
  );
}


export function PaymentsGrid({ payments, onClickPay }) {
  const { role } = usePaymentsStore();
  const isClient = role === 'adevs-client';
  const isAdmin = role === 'adevs-admin';
  const hasRows = payments.length > 0;

  return (
    <StyledCard elevation={1} sx={{ mb: 3 }}>
      <CardHeader
        disableTypography
        sx={(t) => ({
          bgcolor: t.palette.primary.main,
          color: t.palette.primary.contrastText,
          py: 1.5, px: 2,
        })}
        title={
          <Typography variant="h6" sx={(t) => ({ fontWeight: 700, color: t.palette.primary.contrastText })}>
            💳 Pagos del proyecto
          </Typography>
        }
        subheader={
          <Typography variant="body2" sx={(t) => ({ color: t.palette.primary.contrastText, opacity: 0.85 })}>
            {isClient ? "Consulta tu calendario y realiza tus pagos" : "Calendario de pagos del cliente"}
          </Typography>
        }
        action={isAdmin && <AdminActions />}
      />
      <CardContent>
        {!hasRows && (
          <Alert severity="info">Aún no hay pagos programados para este proyecto.</Alert>
        )}

        {hasRows && (
          <ScrollArea>
            <Stack spacing={1.25}>
              {payments.map((p) => {
                const isAdvance = p.kind === "advance" || p.paymentNo === 0;
                const currency = String(p.currency || "MXN").toUpperCase();
                const total = Number(p.amountTotal);

                return (
                  <Box
                    key={p._id}
                    sx={{
                      border: (t) => `1px solid ${t.palette.divider}`,
                      borderRadius: 2, p: 1.25, display: "grid",
                      gridTemplateColumns: "1fr auto", gap: 1,
                      ...(isAdvance ? { bgcolor: "action.hover" } : null),
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        #{isAdvance ? "A" : p.paymentNo} · {isAdvance ? "Anticipo" : "Hito"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isAdvance ? (
                          <>Registrado: {fmtDateTime(p.paidAt) || fmtDateTime(p.dueDate)} · Total: <strong>{fmtMoney(total, currency)}</strong>{p.note ? <> · {p.note}</> : null}</>
                        ) : (
                          <>Vence: {fmtDate(p.dueDate)} {p.deliveryAt && <>· Entrega: {fmtDateTime(p.deliveryAt)} </>}· Total: <strong>{fmtMoney(total, currency)}</strong></>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <StatusChip status={p.status} />
                      {isClient && p.status === 'paid' && p.facturapiInvoiceId && (
                        <Tooltip title="Descargar factura">
                          <IconButton size="small" onClick={() => window.open(`/${role}/api/payments/invoices/${p.facturapiInvoiceId}/zip`, '_blank')}>
                            <GetAppIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!isAdvance && p.deliveryAt && (
                        <Tooltip title={`Entrega programada: ${fmtDateTime(p.deliveryAt)}`}>
                          <Chip size="small" color="info" icon={<EventAvailableIcon />} label="Entrega programada" variant="outlined" />
                        </Tooltip>
                      )}
                      {isClient && p.status !== "paid" && !isAdvance && (
                        <Button size="small" variant="contained" startIcon={<PaymentIcon />} onClick={() => onClickPay(p)}>
                          Pagar ahora
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </ScrollArea>
        )}

        {isClient && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Los montos incluyen IVA y comisiones de procesamiento, tu factura será emitida por correo electrónico 3 días hábiles despues de procesado tu pago.
            </Typography>
          </>
        )}
      </CardContent>
    </StyledCard>
  );
}
